import React, { useState, useEffect } from "react";
import { useIntl, FormattedMessage, FormattedDate } from "react-intl";
import messages from "i18n/messages";
import isEmpty from "lodash/isEmpty";
import range from "lodash/range";
import { useSelector, useDispatch } from "react-redux";
import { Button, Form, FormGroup, Label, FormText, Input } from "reactstrap";
import {
  makeSelectDateById,
  fetchGoodsDateById,
  makeSelectResponseStatus,
} from "logic/dates";
import { makeSelectCurOrg } from "logic/orgs";
import { defaultDateFormat } from "../lib/misc";
import { setStepOneComplete, setManualSeatInfos } from "logic/goods";
import { useFormik } from "formik";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
  section: Yup.string().required("Bitte wählen Sie Ihren Block"),
  row: Yup.string().required("Bitte Wählen Sie Ihren Reihe"),
  seat: Yup.number().required("Bitte Wählen Sie Ihren Platz"),
});

function SelectItem({ formik, name, customRange, limit, handleChange }) {
  const renderOptionByIndex = (index) => (
    <option key={`${name}-${index}`} value={index}>
      {index}
    </option>
  );
  const { formatMessage: f } = useIntl();

  return (
    <FormGroup>
      <Label for={name}>
        <FormattedMessage {...messages[name]} />
      </Label>
      <Input type="select" name={name} onChange={handleChange}>
        <option value="">{f(messages.pleaseSelect)}</option>
        {customRange
          ? customRange.split(",").map(renderOptionByIndex)
          : range(1, limit + 1).map(renderOptionByIndex)}
      </Input>
      {formik.errors[name] && formik.touched[name] && (
        <FormText color="danger">{formik.errors[name]}</FormText>
      )}
    </FormGroup>
  );
}

function SalesClosed() {
  const { formatMessage: f } = useIntl();
  return (
    <div className="tt-book-settings tickets-selection">
      <div className="qty-form-control-price-options open">
        <div className="text-center p-4 mt-2">
          <h4>{f(messages.salesClosed)}</h4>
        </div>
      </div>
    </div>
  );
}

export default function ManualSeatsSelection({ dateId }) {
  const dispatch = useDispatch();
  const { formatMessage: f } = useIntl();
  const org = useSelector(makeSelectCurOrg());
  const error = useSelector(makeSelectResponseStatus());
  // const stepOneComplete = useSelector(makeSelectStepOneComplete())
  const date = useSelector(makeSelectDateById(() => dateId));
  const handleSubmit = (values) => {
    dispatch(setManualSeatInfos(values));
    dispatch(setStepOneComplete());
  };

  const formik = useFormik({
    initialValues: {
      section: "",
      row: "",
      seat: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    dispatch(fetchGoodsDateById(dateId));
  }, [dispatch]);

  if (error && error === 423) return <SalesClosed />;
  if (!date) return <></>;

  let secRange =
    "CERCLE LINKS,CERCLE RECHTS,PARTERRE,TRIBÜNE LINKS,TRIBÜNE MITTE,TRIBÜNE RECHTS";
  let rowRange = null;
  let seatRange = null;
  let rowLimit = 34;
  let seatLimit = 80;
  if (org.short_identifier === "R7J") {
    secRange = "A,B,C,D,E,F,G,H,I,J,K";
    rowRange = range(35).map((i) => `Reihe ${i}`);
    rowRange.splice(0, 1); // no Reihe 0!
    rowRange.splice(24, 0, "Reihe 23A");
    rowRange = rowRange.join(",");
  }
  if (org.short_identifier === "91A") {
    secRange = "Links,Mitte,Rechts";
    rowRange = "-";
    seatRange = formik.values.row && formik.values.row !== "-" ? null : "-";
    // "Reihe 1,Reihe 2,Reihe 3,Reihe 4,Reihe 5,Reihe 6,Reihe 7,Reihe 8,Reihe 9,Reihe 10,Reihe 11,Reihe 12,Reihe 13,Reihe 14,Tisch 1,Tisch 2,Tisch 3,Tisch 4,Tisch 5,Tisch 6,Tisch 7,Tisch 8,Tisch 9,Tisch 10,Tisch 11,Tisch 12,Tisch 13,Tisch 14,Tisch 15,Tisch 16";
    // "Reihe A, Reihe B, Reihe D, Reihe E, Mittelloge Reihe 2,Mittelloge Reihe 4,Mittelloge Reihe 5,Mittelloge Reihe 6,Mittelloge Reihe 8,Mittelloge Reihe 10,Mittelloge Reihe 13,Tisch 1,Tisch 2,Tisch 3,Tisch 4,Tisch 5,Tisch 6,Tisch 7,Tisch 8,Tisch 9,Tisch 10,Tisch 11,Tisch 12,Tisch 13,Tisch 14,Tisch 15,Tisch 16";
    seatLimit = 5;
    if (formik.values.section && formik.values.section === "Links") {
      rowRange =
        "Tisch 1,Tisch 2,Tisch 3,Tisch 4,Tisch 5,Tisch 6,Tisch 7,Tisch 8,Tisch 9,Tisch 10,Tisch 11,Tisch 12,Tisch 13,Tisch 14,Tisch 15,Tisch 16,B,D";
    } else if (formik.values.section && formik.values.section === "Rechts") {
      rowRange =
        "Tisch 1,Tisch 2,Tisch 3,Tisch 4,Tisch 5,Tisch 6,Tisch 7,Tisch 8,Tisch 9,Tisch 10,Tisch 11,Tisch 12,Tisch 13,Tisch 14,Tisch 15,A,C";
    } else if (formik.values.section && formik.values.section === "Mitte") {
      rowRange =
        "Tisch 1,Tisch 2,Tisch 3,Tisch 4,Tisch 5,Tisch 6,Tisch 7,Tisch 8,Reihe 10,Reihe 13";
    }
    if (
      formik.values.row &&
      (formik.values.row === "Reihe 10" || formik.values.row === "Reihe 13")
    ) {
      seatRange = "1,2,5,6,9,10,11";
    }
  }
  if (org.short_identifier === "5CA") {
    secRange =
      "1.Parkett Links,1.Parkett Rechts,2.Parkett Links,2.Parkett Rechts,Tribüne Links,Tribüne Rechts";
    rowLimit = 44;
    seatLimit = 38;
  }
  return (
    <div className="tt-book-settings tickets-selection">
      <div className="qty-form-control-price-options open">
        <div className="text-center p-4 mt-2">
          {!isEmpty(org.portal.goods_title) ? (
            <h4 className="mb-4">{org.portal.goods_title}</h4>
          ) : null}
          <h4>
            {date.title}
            <br />
            <small>
              <FormattedDate value={date.starts_at} {...defaultDateFormat} />
            </small>
          </h4>
          <p className="mt-4 mb-2">{f(messages.selectYourSeatsForDrinks)}</p>
          <Form
            onSubmit={formik.handleSubmit}
            className="mt-4 select-seats-for-drinks"
          >
            <SelectItem
              name="section"
              formik={formik}
              value={formik.values.section}
              customRange={secRange}
              handleChange={formik.handleChange}
            />
            <SelectItem
              name="row"
              formik={formik}
              value={formik.values.row}
              limit={rowLimit}
              customRange={rowRange}
              handleChange={formik.handleChange}
            />
            <SelectItem
              name="seat"
              formik={formik}
              value={formik.values.seat}
              limit={seatLimit}
              customRange={seatRange}
              handleChange={formik.handleChange}
            />

            <div className="mt-4">
              <Button size="lg" type="submit" color="primary">
                <FormattedMessage {...messages.continue} />
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}
